import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { memo, useMemo } from "react";
import { ReactComponent as PersonIcon } from '../../../assets/icons/person.svg';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import { Dashboard, DashboardQuerySource } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        height: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    avatar: {
        width: 48,
        height: 48,
        minWidth: 48,
        borderRadius: '50%',
        background: 'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    initials: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: 'white',
    },
    name: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 700,
        color: '#334155',
    },
    infoText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    infoIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        color: theme.colors.neutral['400'],
    },
}));

const DealSourceContentCard: React.FC<{ dashboard: Dashboard, onClose: () => void }> = ({ dashboard, onClose }) => {
    const classes = useStyles();
    const { dashboardSource } = useBackwardsCompatible();

    const source = useMemo(() => {
        const dealSource = dashboardSource(dashboard);

        return (dealSource?.name !== DashboardQuerySource.LINKEDIN) ? dealSource : undefined;
    }, [dashboard, dashboardSource]);

    const initials = useMemo(() =>
        source?.name ? source.name.split(' ').slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('') : ''
    , [source]);

    return (<>
        <Stack className={classes.card}>
            <Stack direction="row" spacing={2} p={2} width="100%" alignItems="flex-start">
                <Box className={classes.avatar}>
                    {initials
                        ? <Typography className={classes.initials}>{initials}</Typography>
                        : <PersonIcon style={{ width: 32, height: 32, stroke: '#64748b' }} />
                    }
                </Box>
                <Stack direction="column" spacing={0.5} minWidth={0} flex={1}>
                    <Typography className={classes.name}>
                        {source?.name || '[No source]'}
                    </Typography>
                    {!!source?.email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <MailOutlineIcon className={classes.infoIcon} />
                            <Typography className={classes.infoText}>
                                {source.email}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <IconButton onClick={() => onClose()} disableRipple sx={{ mt: -0.5 }}>
                    <CloseIcon fontSize="small" sx={{ color: '#64748b' }} />
                </IconButton>
            </Stack>
        </Stack>
    </>);
};

export default memo(DealSourceContentCard);
